import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { CheckCircle2, Laptop, Loader2, ShieldCheck, XCircle } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'
import { Field, FormAlert } from '@/components/app/form'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { authClient } from '@/lib/auth-client'
import { sessionQuery, workspacesQuery } from '@/lib/session'

export const Route = createFileRoute('/device')({
  validateSearch: z.object({ user_code: z.string().optional() }),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(sessionQuery).catch(() => null)
    if (!session) throw redirect({ to: '/sign-in', search: { redirect: location.href } })
    return { session }
  },
  head: () => ({ meta: [{ title: 'Connect desktop · Nebueye' }] }),
  component: DevicePage,
})

/** XXXX-XXXX for display; the server ignores the dash. */
const pretty = (code: string) => {
  const c = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c
}

function DevicePage() {
  const { user_code } = Route.useSearch()
  const navigate = Route.useNavigate()
  return (
    <div className="nebula-soft grid min-h-dvh place-items-center px-6 py-12">
      <div className="animate-in fade-in slide-in-from-bottom-2 w-full max-w-[440px] space-y-8 duration-500">
        <Logo />
        {user_code ? (
          <Review code={user_code} onReset={() => navigate({ search: {}, replace: true })} />
        ) : (
          <EnterCode onSubmit={(code) => navigate({ search: { user_code: code }, replace: true })} />
        )}
      </div>
    </div>
  )
}

function EnterCode({ onSubmit }: { onSubmit: (code: string) => void }) {
  const [code, setCode] = useState('')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const clean = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    if (clean) onSubmit(clean)
  }
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">Connect your desktop app</h1>
        <p className="text-muted-foreground text-[15px] leading-6">Enter the code shown in Nebueye desktop under Settings → Cloud.</p>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Code"
          value={code}
          onChange={(e) => setCode(pretty(e.target.value))}
          placeholder="XXXX-XXXX"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="[&_input]:id-mono [&_input]:h-12 [&_input]:text-center [&_input]:text-lg [&_input]:tracking-[0.2em]"
          autoFocus
        />
        <Button type="submit" size="lg" className="w-full" disabled={code.replace(/[^a-zA-Z0-9]/g, '').length < 6}>
          Continue
        </Button>
      </form>
    </div>
  )
}

function Review({ code, onReset }: { code: string; onReset: () => void }) {
  const { session } = Route.useRouteContext()
  const queryClient = useQueryClient()
  const workspaces = useQuery(workspacesQuery).data ?? []
  const [pending, setPending] = useState<'approve' | 'deny'>()
  const [error, setError] = useState<string>()
  const [done, setDone] = useState<'approved' | 'denied'>()

  const request = useQuery({
    queryKey: ['device-code', code],
    queryFn: async () => {
      const { data, error } = await authClient.device({ query: { user_code: code } })
      if (error) throw new Error(error.error_description ?? 'This code is not valid.')
      return data
    },
    retry: false,
    staleTime: Infinity,
  })

  async function respond(kind: 'approve' | 'deny') {
    setPending(kind)
    setError(undefined)
    const { error } = kind === 'approve' ? await authClient.device.approve({ userCode: code }) : await authClient.device.deny({ userCode: code })
    setPending(undefined)
    if (error) return setError(error.error_description ?? 'That did not work. Start again from the desktop app.')
    setDone(kind === 'approve' ? 'approved' : 'denied')
    queryClient.invalidateQueries({ queryKey: ['devices'] })
  }

  if (done === 'approved') {
    return (
      <Result icon={<CheckCircle2 className="text-status-success size-7" />} title="Desktop connected">
        Go back to Nebueye desktop to finish. It will be ready in a few seconds, and you can turn Cloud Sync on from there.
      </Result>
    )
  }
  if (done === 'denied') {
    return (
      <Result icon={<XCircle className="text-status-danger size-7" />} title="Request denied">
        Nothing was connected. If that was you, start again from the desktop app.
      </Result>
    )
  }
  if (request.isPending) return <Loader2 className="text-muted-foreground size-5 animate-spin" aria-label="Checking code" />
  if (request.isError || request.data?.status !== 'pending') {
    return (
      <div className="space-y-5">
        <div className="space-y-2">
          <h1 className="text-headline font-semibold">This code can't be used</h1>
          <p className="text-muted-foreground text-[15px] leading-6">
            {request.data && request.data.status !== 'pending'
              ? 'It has already been used. Start sign-in again from the desktop app to get a new one.'
              : 'It may have expired (codes last 10 minutes) or been mistyped. Check the code in the desktop app.'}
          </p>
        </div>
        <Button variant="outline" onClick={onReset}>
          Enter a different code
        </Button>
      </div>
    )
  }

  const workspace = workspaces.find((w) => w.id === session.session.activeOrganizationId) ?? workspaces[0]
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">Connect this desktop?</h1>
        <p className="text-muted-foreground text-[15px] leading-6">
          Nebueye desktop is asking to connect to your account, <span className="text-foreground font-medium">{session.user.email}</span>.
        </p>
      </div>

      <div className="bg-card space-y-4 rounded-xl border p-5 shadow-xs">
        <div className="flex items-center gap-3">
          <span className="bg-accent text-accent-foreground grid size-10 place-items-center rounded-lg">
            <Laptop className="size-5" />
          </span>
          <div>
            <p className="text-muted-foreground text-[13px]">Code shown in the desktop app</p>
            <p className="id-mono text-lg font-semibold tracking-[0.18em]">{pretty(code)}</p>
          </div>
        </div>
        <ul className="text-muted-foreground space-y-2 border-t pt-4 text-[13px] leading-5">
          <li className="flex gap-2">
            <ShieldCheck className="text-status-success mt-0.5 size-4 shrink-0" />
            <span>
              It can upload orders to {workspace ? <strong className="text-foreground font-medium">{workspace.name}</strong> : 'your Vault'} once you
              turn on Cloud Sync.
            </span>
          </li>
          <li className="flex gap-2">
            <ShieldCheck className="text-status-success mt-0.5 size-4 shrink-0" />
            It can't change your password, invite people or see other Vaults.
          </li>
          <li className="flex gap-2">
            <ShieldCheck className="text-status-success mt-0.5 size-4 shrink-0" />
            You can disconnect it any time in Settings → Integrations.
          </li>
        </ul>
      </div>

      {error && <FormAlert>{error}</FormAlert>}
      <div className="flex gap-2">
        <Button size="lg" className="flex-1" onClick={() => respond('approve')} disabled={!!pending}>
          {pending === 'approve' && <Loader2 className="animate-spin" />}
          Connect
        </Button>
        <Button size="lg" variant="outline" onClick={() => respond('deny')} disabled={!!pending}>
          Deny
        </Button>
      </div>
      <p className="text-subtle text-xs leading-5">Only connect if you started this yourself and the code matches the one on your screen.</p>
    </div>
  )
}

function Result({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-5">
      {icon}
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">{title}</h1>
        <p className="text-muted-foreground text-[15px] leading-6">{children}</p>
      </div>
      <Button asChild variant="outline">
        <Link to="/settings" search={{ tab: 'integrations' }}>
          See connected desktops
        </Link>
      </Button>
    </div>
  )
}
